import { ServiceTypeItem, ServiceTypeList } from "src/types";

const isRecord = (value: unknown): value is Record<string, unknown> => Boolean(value) && typeof value === "object";

export const normalizeServiceTypes = (rawServiceTypes: unknown, clientId?: string): ServiceTypeList => {
  if (!rawServiceTypes) {
    return {};
  }

  if (Array.isArray(rawServiceTypes)) {
    const normalizedList = rawServiceTypes.filter(Boolean) as ServiceTypeItem[];
    return clientId ? { [clientId]: normalizedList } : { "": normalizedList };
  }

  if (isRecord(rawServiceTypes)) {
    const nestedServiceTypes = rawServiceTypes.serviceTypes;
    if (Array.isArray(nestedServiceTypes)) {
      const normalizedList = nestedServiceTypes.filter(Boolean) as ServiceTypeItem[];
      return clientId ? { [clientId]: normalizedList } : { "": normalizedList };
    }

    const normalizedMap = Object.entries(rawServiceTypes).reduce<ServiceTypeList>((acc, [key, value]) => {
      if (Array.isArray(value)) {
        acc[key] = value.filter(Boolean) as ServiceTypeItem[];
      }
      return acc;
    }, {});

    if (Object.keys(normalizedMap).length) {
      return normalizedMap;
    }

    return {};
  }

  return {};
};

export const getServiceTypesForClient = (
  serviceTypes: ServiceTypeList | undefined,
  clientId: string,
): ServiceTypeItem[] => {
  if (!serviceTypes) {
    return [];
  }

  const exactMatch = serviceTypes[clientId];
  if (exactMatch?.length) {
    return exactMatch;
  }

  const firstAvailableBucket = Object.values(serviceTypes).find((items) => Array.isArray(items) && items.length);
  return firstAvailableBucket || [];
};

export type HighRiskClassificationOption = {
  id: string;
  label: string;
  value: string;
};

export const normalizeHighRiskClassifications = (rawClassifications: unknown): HighRiskClassificationOption[] => {
  if (!rawClassifications) {
    return [];
  }

  if (Array.isArray(rawClassifications)) {
    return rawClassifications
      .filter(Boolean)
      .map((item) => {
        if (isRecord(item)) {
          const label = String(item.label ?? item.value ?? "").trim();
          const value = String(item.value ?? item.label ?? "").trim();
          const id = label || value;
          return id ? { id, label: label || id, value: value || id } : null;
        }

        const value = String(item).trim();
        return value ? { id: value, label: value, value } : null;
      })
      .filter((item): item is HighRiskClassificationOption => Boolean(item));
  }

  if (isRecord(rawClassifications)) {
    return Object.entries(rawClassifications)
      .filter(([, value]) => isRecord(value) || typeof value === "string")
      .map(([key, value]) => {
        if (isRecord(value)) {
          const label = String(value.label ?? value.value ?? key).trim();
          const rawValue = String(value.value ?? value.label ?? key).trim();
          return { id: label || rawValue || key, label: label || rawValue || key, value: rawValue || label || key };
        }

        const itemValue = String(value).trim();
        return { id: itemValue || key, label: itemValue || key, value: itemValue || key };
      });
  }

  return [];
};
